/**
 * The CONVERSATION LINTER of #204 (scenario-succes-conduite.md §15.1): the
 * mechanical checks of every message the interviewer wrote, in one pure module.
 *
 * ## Why it lives in `shared`, and why it is pure
 *
 * The first reader is the MCP tool `lint_conversation`, which reads sessions and
 * replays for the measure of #204; the second will be the continuous linter of
 * §12, on the page of a session and in the weekly aggregate. A measure each
 * reader wrote for itself would not be the same measure twice, which is the one
 * thing an instrument must be - so it is written once, here, with no clock, no
 * random, no I/O and no dependency, in the way `figures.ts` is for the screens.
 *
 * ## Two kinds of check, two readings, and what each one costs
 *
 * - SIX HARD FAULTS of the spec, SEVEN codes here (the fourth row, « question
 *   négative ou à deux options », is two codes): a thing no message may do, tested
 *   on EVERY message - the turns of the protocol and the bubbles of the pages
 *   played in the conversation alike. Target: none.
 * - SIX SOFT TARGETS, tested on the CONVERSATION OF THE PROTOCOL only: a
 *   bubble has no written answer and plays another part, so it enters none of
 *   them. The farewell counts where a rule says so and stays out of the rates
 *   that bear on questions; each target below says which.
 * - TWO READINGS, on the same turns, that no target bounds and no gate counts
 *   (§15.1, decisions of Nicolas, 2026-10-24): the interviewer's share of the
 *   words, beside the words of the person's answers, and the confidences that do
 *   not stand in a bubble of their own. Both were soft targets until the reference
 *   of the lot H showed what they measured: the share follows the person (-0.96
 *   with their words per turn), and « two bubbles only after a confidence » was
 *   met by every replay, since none wrote two paragraphs.
 *
 * ## What the thresholds are
 *
 * `LINT_TARGETS` holds the numbers, each with its place in §15.1. They come from
 * the analysis of #204, and the lot H calibrates them on the reference; a
 * threshold is changed here and nowhere else. The frequency of the words, spoken
 * and written, to spot a language too written (a base like Lexique) is to be
 * built and is not in this module.
 *
 * ## The comparison is on accents-less tokens
 *
 * People type without accents (« ca » for « ça », « hate » for « hâte »), so an
 * echo or a copy is looked for on the tokens once their diacritics are removed.
 * Two checks read the accents before they are removed, and say so where they do:
 * « où » is not the « ou » of two options, and the folded form of « hé » must
 * still be told from a word.
 */

/* ------------------------------------------------------------- the constants */

/**
 * The step whose FIRST turn opens the phase of the moment (§3.8, P6): until then
 * the character may speak of itself - the distractor of Patrick, small talk,
 * Accueil, Le sujet - and no confidence counts. From then on it stays on the
 * person, with at most two short confidences.
 */
export const MOMENT_STEP_ID = 'le-succes';

/**
 * The one step where a strong emotion may be named, once (§3.2, P12): « Moi, rien
 * que d'y penser, ça m'aurait fait un peu peur… et vous ? ». It is the only
 * exception to « jamais une question négative » (§3.8): it holds nowhere else, and
 * for the first negative question of the conversation only.
 */
export const P12_STEP_ID = 'le-sujet';

/**
 * The one step where a question may offer two options, once (§3.3, R2): when no
 * time comes, a single follow-up proposes leads - a recent time, a time with
 * someone, a moment stronger than the others - and leads are options by nature.
 * It is the only exception to « jamais deux options proposées » (§3.8), on the
 * model of P12: it holds nowhere else, and for the first question with two options
 * of the step only (decision of Nicolas, 2026-10-24).
 */
export const R2_STEP_ID = 'le-succes';

/**
 * The thresholds of §15.1, each as a number and named by the target it bounds.
 * The two readings have none: they are read, not met.
 *
 * `max` is a ceiling to be met (`<=`); a ratio is a share in [0, 1].
 */
export const LINT_TARGETS = {
  /** §15.1, §3.8: two confidences at most, never two turns in a row. */
  confidences: { max: 2, consecutivePairsMax: 0 },
  /** §15.1: a median of 18 words at most per message, 35 at the maximum. */
  words: { medianMax: 18, max: 35 },
  /** §15.1, §1: « réaction puis question », one message in two at most, never three in a row. */
  mould: { shareMax: 0.5, longestStreakMax: 2 },
  /** §15.1: a question copied from an example of the protocol, 20 % at most; a run of 5 tokens. */
  copied: { shareMax: 0.2, minTokens: 5 },
  /** §15.1, P8: an exact echo, four words or more of the person taken as they were - none. */
  echo: { max: 0, minTokens: 4 },
  /** §15.1: the same opening never in two messages out of three. */
  openings: { max: 0 },
} as const;

/** The seven codes of the hard faults, in the order a message is checked and a fault listed. */
export const HARD_FAULT_CODES = [
  'interjection',
  'quotes',
  'two-questions',
  'negative-question',
  'two-options',
  'first-name-unsaid',
  'self-introduction',
] as const;
export type HardFaultCode = (typeof HARD_FAULT_CODES)[number];

/** The six soft targets, by the key of their block in `ConversationLint.soft`. */
export const SOFT_TARGET_KEYS = [
  'confidences',
  'words',
  'mould',
  'copied',
  'echo',
  'openings',
] as const;
export type SoftTargetKey = (typeof SOFT_TARGET_KEYS)[number];

/** How long the excerpt of a fault may be, in characters (the ellipsis included). */
const EXCERPT_MAX = 160;

/**
 * The character classes of the tokenising. They stand BEFORE the word lists below
 * on purpose: the lists are folded when the module loads, and a `const` read
 * before its line is a ReferenceError, not an undefined.
 */

/** Whatever is not a letter, a digit or a hyphen: the apostrophes, straight and curly, are among it. */
const NOT_A_WORD_CHARACTER = /[^\p{L}\p{N}-]+/gu;
const EDGE_HYPHENS = /^-+|-+$/g;
const COMBINING_MARKS = /\p{M}+/gu;

/* ------------------------------------------------------------------ the words */

/**
 * The interjections that open a message (§3.8, P7): « Ah », « Oh », « Tiens ». The
 * first four of the last row need a punctuation behind them to be one -
 * « Tiens, … », « Bon ! » - because « bon appétit » and « ben oui » open nothing
 * of the kind; the others are one whatever follows, as long as it is not a letter.
 */
export const INTERJECTIONS: readonly string[] = [
  'ah',
  'oh',
  'ha',
  'ho',
  'hé',
  'eh',
  'ouh',
  'ouah',
  'waouh',
  'wouah',
  'oups',
  'aïe',
  'ouf',
  'hum',
  'euh',
  'tiens',
  'bah',
  'ben',
  'bon',
];

/** The four interjections that count only when a punctuation follows (folded, like the comparison). */
const NEEDS_PUNCTUATION = new Set(['tiens', 'bah', 'ben', 'bon']);

/**
 * The words of the negative (§3.8, P12): what makes a question « négative » beyond
 * a negation - fear, hardness, regret, sadness, anger. Each family carries the
 * forms the spec names (« et dérivés », « et ses formes ») written out, since a
 * token is compared whole: the feminine and the plural of an adjective, the
 * conjugation of a verb, the noun that comes from it.
 *
 * Compared without diacritics, like every comparison of the module.
 */
export const NEGATIVE_WORDS: readonly string[] = [
  // fear
  'peur peurs effrayé effrayée effrayés effrayées effrayant effrayante effrayants effrayantes',
  'terrifié terrifiée terrifiés terrifiées terrifiant terrifiante terrifiants terrifiantes terrifier terrifie terrifies terrifient terrifiait terrifiaient',
  'angoisse angoisses angoissé angoissée angoissés angoissées angoissant angoissante angoissants angoissantes angoisser angoissent angoissait angoissaient',
  'stress stressé stressée stressés stressées stressant stressante stressants stressantes stresser stresse stresses stressent stressait stressaient',
  'inquiet inquiète inquiets inquiètes inquiétude inquiétudes',
  'crainte craintes craindre crains craint craignons craignez craignent craignais craignait craignaient craignant craindrait craindra',
  'panique paniques paniqué paniquée paniqués paniquées',
  'vertige vertiges',
  // hardness and difficulty
  'dur dure durs dures difficile difficiles difficulté difficultés',
  'compliqué compliquée compliqués compliquées compliquer complique compliques compliquent compliquait compliquaient complication complications',
  'problème problèmes galère galères galérer galèrent galérez galérons galérais galérait galéraient galéré galérée galérés galérées galérant',
  'pénible pénibles obstacle obstacles échec échecs raté ratée ratés ratées',
  // regret, sadness, disappointment
  'regret regrets regretter regrette regrettes regrettent regrettez regrettons regrettait regrettais regrettaient regretté regrettée regrettés regrettées regrettant',
  'triste tristes tristesse tristesses',
  'déçu déçue déçus déçues décevoir déçois déçoit déçoivent décevait décevaient décevant décevante décevants décevantes déception déceptions',
  'frustré frustrée frustrés frustrées frustrer frustre frustres frustrent frustrait frustraient frustrant frustrante frustrants frustrantes frustration frustrations',
  // pain, anger, shame
  'souffrir souffre souffres souffrons souffrez souffrent souffrait souffrais souffraient souffert soufferte souffrant souffrante souffrance souffrances',
  'douleur douleurs',
  'énervé énervée énervés énervées énerver énerve énerves énervent énervait énervaient énervant énervante énervants énervantes énervement',
  'colère colères honte hontes',
].flatMap((line) => line.split(' '));

/**
 * The function words of everyday French: articles and determiners, prepositions,
 * pronouns, conjunctions, the forms of être and avoir, and the adverbs that link
 * or soften. What is NOT here is a FULL WORD (`isFullWord`): every other verb, noun
 * and adjective, whatever its size, so « voulez » and « personne » count as words.
 *
 * Two tokens are here for the apostrophe: « aujourd'hui » and « quelqu'un » are
 * cut into pieces by the tokenisation, and their pieces are not words.
 */
export const STOP_WORDS: ReadonlySet<string> = new Set(
  [
    // articles and determiners
    'le la les l un une des du de d au aux ce cet cette ces mon ma mes ton ta tes son sa ses notre nos votre vos leur leurs',
    'quel quelle quels quelles quelque quelques chaque plusieurs certain certains certaine certaines aucun aucune tout tous toute toutes autre autres même mêmes tel telle tels telles',
    // prepositions
    'à a en dans par pour sur sous avec sans chez vers entre contre depuis pendant avant après devant derrière selon malgré parmi sauf hors dès jusque jusqu envers durant via près lors outre',
    // pronouns
    'je j tu il elle on nous vous ils elles me m te t se s lui y moi toi soi eux celui celle ceux celles ceci cela ça ca c qui que qu quoi dont où lequel laquelle lesquels lesquelles duquel desquels auquel auxquels quiconque rien chacun chacune quelqu',
    // conjunctions
    'et ou ni mais donc or car si comme quand lorsque lorsqu puisque puisqu parce quoique quoiqu tandis afin soit tant sinon',
    // the forms of être and avoir
    'être suis es est sommes êtes sont étais était étions étiez étaient fus fut fûmes fûtes furent serai seras sera serons serez seront serais serait serions seriez seraient sois soyons soyez soient été étant',
    'avoir ai as avons avez ont avais avait avions aviez avaient eus eut eûmes eûtes eurent aurai auras aura aurons aurez auront aurais aurait aurions auriez auraient aie aies ait ayons ayez aient eu ayant',
    // adverbs that link, soften or count
    'alors ainsi aussi puis ensuite enfin pourtant cependant toutefois néanmoins finalement justement surtout encore déjà toujours jamais souvent parfois peut-être très trop assez plutôt vraiment tellement bref voilà voici là ici là-bas comment pourquoi combien oui non peu beaucoup moins plus bien mal pas ne n point guère seulement presque maintenant bientôt hier demain ailleurs partout dedans dehors dessus dessous aujourd hui',
  ].flatMap((line) => line.split(' ')),
);

/** The list above, without diacritics: the form every comparison is made on. */
const FOLDED_STOP_WORDS: ReadonlySet<string> = new Set(
  [...STOP_WORDS].map((word) => withoutDiacritics(word)),
);

/** The same for the interjections. */
const FOLDED_INTERJECTIONS: ReadonlySet<string> = new Set(
  INTERJECTIONS.map((word) => withoutDiacritics(word)),
);

/** And for the words of the negative. */
const FOLDED_NEGATIVE_WORDS: ReadonlySet<string> = new Set(
  NEGATIVE_WORDS.map((word) => withoutDiacritics(word)),
);

/* -------------------------------------------------------------- the tokenising */

/**
 * The words of a text: lower case, every character that is not a letter, a digit
 * or a hyphen made a space, and a token kept only when something is left of it.
 * « c'est » is two words, « jour-là » and « Jean-Jacques » one: the hyphen binds,
 * the apostrophe does not. A hyphen at the EDGE of a token is typography - a
 * dialogue dash, a bullet - and is not part of the word.
 *
 * The text is put in its composed form first, so an accent typed as two
 * characters does not cut its word in two.
 */
export function lintWords(text: string): string[] {
  return text
    .normalize('NFC')
    .toLowerCase()
    .replace(NOT_A_WORD_CHARACTER, ' ')
    .split(' ')
    .map((token) => token.replace(EDGE_HYPHENS, ''))
    .filter((token) => token !== '');
}

/** The text without its diacritics: « ça » is « ca », « hâte » is « hate ». */
export function withoutDiacritics(text: string): string {
  return text.normalize('NFD').replace(COMBINING_MARKS, '');
}

/**
 * The words a COMPARISON is made on (an echo, a copy, a name): the same tokens,
 * then without diacritics. The two arrays of one text have the same length and
 * the same order, so a run found on this one can be written back with the other.
 */
export function comparisonWords(text: string): string[] {
  return lintWords(text).map((token) => withoutDiacritics(token));
}

/**
 * Whether a token is a function word. A hyphenated token whose parts are all
 * function words is one too: « est-ce », « avez-vous », « a-t-il » are the verb
 * and its pronoun turned round, and counting them as words would make an echo of
 * « est-ce que vous avez » out of nothing.
 */
export function isStopWord(token: string): boolean {
  const folded = withoutDiacritics(token.toLowerCase());
  if (FOLDED_STOP_WORDS.has(folded)) return true;
  return folded.includes('-') && folded.split('-').every((part) => FOLDED_STOP_WORDS.has(part));
}

/**
 * A FULL WORD: a token that is not a function word and has at least four
 * characters. The unit an echo or a copy has to hold at least one of, so that a run
 * of « avec vous dans le » is nobody's phrase.
 */
export function isFullWord(token: string): boolean {
  return withoutDiacritics(token).length >= 4 && !isStopWord(token);
}

/** A sentence of a text, and where it stands in it. */
interface Span {
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

/**
 * The dialogue dashes - the hyphen, the en dash, the em dash - by code point: the
 * repository refuses a long dash in its files, and this one names them.
 */
const DIALOGUE_DASHES = String.fromCodePoint(0x2d, 0x2013, 0x2014);

/**
 * The gap between two sentences: spaces that follow a `.`, a `!`, a `?` or a `…`
 * (and the closing quotes or brackets behind it), and that precede a capital, an
 * opening quote or a dialogue dash. A `…` followed by a lower case letter does NOT
 * cut - « un peu peur… et vous ? » is one sentence, which is how the question of
 * P12 reads as the negative question it is.
 */
const SENTENCE_GAP = new RegExp(
  `(?<=[.!?…]["»”')\\]]*)\\s+(?=[${DIALOGUE_DASHES}\\p{Lu}«“"„‹])`,
  'gu',
);

function pushSpan(spans: Span[], text: string, from: number, to: number): void {
  const raw = text.slice(from, to);
  const trimmed = raw.trim();
  if (trimmed === '') return;
  const start = from + (raw.length - raw.trimStart().length);
  spans.push({ text: trimmed, start, end: start + trimmed.length });
}

/** The sentences of a text with their place in it; a line break cuts too. */
function sentenceSpans(text: string): Span[] {
  const spans: Span[] = [];
  let lineStart = 0;
  for (const line of text.split('\n')) {
    let from = 0;
    for (const gap of line.matchAll(SENTENCE_GAP)) {
      pushSpan(spans, text, lineStart + from, lineStart + gap.index);
      from = gap.index + gap[0].length;
    }
    pushSpan(spans, text, lineStart + from, lineStart + line.length);
    lineStart += line.length + 1;
  }
  return spans;
}

/** The sentences of a text (see `SENTENCE_GAP`). */
export function lintSentences(text: string): string[] {
  return sentenceSpans(text).map((span) => span.text);
}

const CLOSING_CHARACTERS = /[\s"»”')\]]+$/u;
const ENDS_ASKING = /\?[?!.…]*$/u;

/**
 * Whether a sentence is a question: it ends with a `?`, the closing quotes and
 * the spaces behind it ignored, and the marks that may follow it - « ?! », « ?… ».
 */
export function isQuestion(sentence: string): boolean {
  return ENDS_ASKING.test(sentence.replace(CLOSING_CHARACTERS, ''));
}

/** The sentences of a text that are questions. */
export function lintQuestions(text: string): string[] {
  return lintSentences(text).filter(isQuestion);
}

const QUESTION_MARKS = /\?[?!]*/g;

/** How many questions a message asks: the runs of `?`, « ?! » and « ?? » counting once. */
export function questionCount(text: string): number {
  return text.match(QUESTION_MARKS)?.length ?? 0;
}

/** The paragraphs of a text: cut at the empty line. */
export function lintParagraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph !== '');
}

/* ----------------------------------------------------------------- the runs */

/**
 * An index of the n-grams of a sequence of tokens - which is where a short text
 * meets a long one (a message and a protocol, a message and the answers before
 * it) without comparing them token by token. Several sources can be put in one
 * index: an empty token stands between two of them, and no token of a text is
 * empty, so a run never crosses from one to the next.
 */
interface GramIndex {
  readonly size: number;
  readonly tokens: string[];
  readonly sources: number[];
  readonly grams: Map<string, number[]>;
}

function newGramIndex(size: number): GramIndex {
  return { size, tokens: [], sources: [], grams: new Map() };
}

/** Puts a source in the index, `source` being whatever the caller wants to read back (a turn). */
function addSource(index: GramIndex, tokens: readonly string[], source: number): void {
  const base = index.tokens.length;
  for (const token of tokens) {
    index.tokens.push(token);
    index.sources.push(source);
  }
  for (let start = 0; start + index.size <= tokens.length; start += 1) {
    const key = tokens.slice(start, start + index.size).join(' ');
    const known = index.grams.get(key);
    if (known === undefined) index.grams.set(key, [base + start]);
    else known.push(base + start);
  }
  index.tokens.push('');
  index.sources.push(source);
}

/** A maximal run of tokens a query shares with an index. */
interface Run {
  /** Where it starts in the query. */
  readonly from: number;
  readonly length: number;
  /** The source it was found in. */
  readonly source: number;
}

/**
 * The maximal runs of at least `index.size` tokens that `query` shares with the
 * index - each run once, extended both ways as far as the two sequences agree.
 */
function runsAgainst(index: GramIndex, query: readonly string[]): Run[] {
  const runs: Run[] = [];
  const seen = new Set<string>();
  for (let start = 0; start + index.size <= query.length; start += 1) {
    const places = index.grams.get(query.slice(start, start + index.size).join(' '));
    if (places === undefined) continue;
    for (const place of places) {
      let before = 0;
      while (
        start - before > 0 &&
        place - before > 0 &&
        query[start - before - 1] === index.tokens[place - before - 1]
      ) {
        before += 1;
      }
      let after = index.size;
      while (
        start + after < query.length &&
        place + after < index.tokens.length &&
        query[start + after] === index.tokens[place + after]
      ) {
        after += 1;
      }
      const key = `${start - before}:${place - before}`;
      if (seen.has(key)) continue;
      seen.add(key);
      runs.push({
        from: start - before,
        length: before + after,
        source: index.sources[place] ?? 0,
      });
    }
  }
  return runs;
}

/** The longest of the runs that hold a full word - the ones that count - or null. */
function longestFullRun(
  runs: readonly Run[],
  query: readonly string[],
  keep: (run: readonly string[]) => boolean = () => true,
): Run | null {
  let best: Run | null = null;
  for (const run of runs) {
    const tokens = query.slice(run.from, run.from + run.length);
    if (!tokens.some(isFullWord) || !keep(tokens)) continue;
    if (best === null || run.length > best.length) best = run;
  }
  return best;
}

/* -------------------------------------------------------------- the excerpts */

/** An excerpt: on one line, and no longer than `EXCERPT_MAX`, the ellipsis counted. */
function excerptOf(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= EXCERPT_MAX) return flat;
  let end = EXCERPT_MAX - 1;
  // Never cut a character in two: an emoji is a pair of code units.
  const last = flat.charCodeAt(end - 1);
  if (last >= 0xd800 && last <= 0xdbff) end -= 1;
  return `${flat.slice(0, end)}…`;
}

/** The sentence that holds a position of the text, or the last one that began before it. */
function spanAround(spans: readonly Span[], position: number): Span | null {
  let found: Span | null = null;
  for (const span of spans) {
    if (span.start > position) break;
    found = span;
  }
  return found;
}

/** The excerpt of the sentence around a position, or of the whole text when there is none. */
function sentenceExcerpt(text: string, spans: readonly Span[], position: number): string {
  return excerptOf(spanAround(spans, position)?.text ?? text);
}

/* ------------------------------------------------------------ the hard checks */

const LEADING_WORD = /^[^\p{L}\p{N}]*(\p{L}+)/u;
const FOLLOWED_BY_PUNCTUATION = /^\s*[,;:.!?…]/u;

/** Whether the word that opens a message is an interjection, given what follows it. */
function isInterjection(word: string, rest: string): boolean {
  const folded = withoutDiacritics(word.toLowerCase());
  if (!FOLDED_INTERJECTIONS.has(folded)) return false;
  return !NEEDS_PUNCTUATION.has(folded) || FOLLOWED_BY_PUNCTUATION.test(rest);
}

/** The quotes of a message (§3.8, P8): never the typographic apostrophe. */
const QUOTES = /["«»“”„‹›]/u;

/** What links a lead to the question it leads up to, besides a colon or a semicolon: « , alors », « , donc ». */
const CLAUSE_LINK = /,\s*(?:alors|donc)\s+/giu;

/**
 * The clause that CARRIES a question, when its sentence leads up to it: what follows
 * the last colon or semicolon - « je n'arrive pas à me décider : voudriez-vous bien
 * m'aider à choisir ? » - and then what follows the last « , alors » or « , donc » -
 * « ..., alors voudriez-vous bien m'aider ? ». What stands before is a statement of
 * its own, the interviewer saying why it asks, and a negation there is the
 * statement's, not the question's: the opening bubble of a page that says it cannot
 * choose is no negative question.
 */
function questionClause(question: string): string {
  const cut = Math.max(question.lastIndexOf(':'), question.lastIndexOf(';'));
  const afterCut = cut === -1 ? question : question.slice(cut + 1);
  let from = 0;
  for (const link of afterCut.matchAll(CLAUSE_LINK)) from = link.index + link[0].length;
  return afterCut.slice(from);
}

/**
 * The words that open a subordinate, so that a `ne` right behind one is the
 * subordinate's and not the question's: « un jour où rien ne presse », « quand plus
 * personne ne vous attend ». « où » is read with its accent, like the `ou` of two
 * options: the « ou » of a choice opens no subordinate. « lorsqu » is « lorsqu'il »
 * cut at the apostrophe.
 */
const SUBORDINATORS: ReadonlySet<string> = new Set(['où', 'quand', 'lorsque', 'lorsqu']);

/** How many tokens before a `ne` its subordinator may stand: « où rien ne », « quand plus personne ne ». */
const SUBORDINATE_REACH = 4;

/** How many tokens after a `ne` its `que` may stand: « il n'y a plus qu'à attendre ». */
const RESTRICTION_REACH = 7;

/**
 * The words that govern an infinitive, folded - « à » is `a` - so that the `ne pas`
 * behind one tells what NOT to do and not what is not so: « à ne pas le laisser
 * tranquille », « pour ne pas le perdre ».
 */
const INFINITIVE_GOVERNORS: ReadonlySet<string> = new Set(['a', 'de', 'd', 'pour', 'sans']);

/**
 * What the verb « durer » follows - « ça dure », « qui dure », « il dure » - folded. The
 * hardness of the negative words is an adjective that follows a noun or a copula:
 * « une journée dure », « c'était dur ».
 */
const SUBJECTS_OF_DURER: ReadonlySet<string> = new Set([
  'ca',
  'cela',
  'qui',
  'il',
  'elle',
  'ce',
  'on',
  'que',
  'qu',
]);

/** Whether the token at `at` is the `ne` of a negation: `ne`, or the `n` of « n' » except in « n'importe ». */
function isNe(tokens: readonly string[], at: number): boolean {
  const token = tokens[at];
  return token === 'ne' || (token === 'n' && tokens[at + 1] !== 'importe');
}

/**
 * Whether the `ne` at `at` denies nothing of the question: it is the restriction of
 * « ne... que » (a `que` within `RESTRICTION_REACH` tokens after it), or a
 * subordinate holds it (a subordinator within `SUBORDINATE_REACH` tokens before it).
 */
function isNeApart(tokens: readonly string[], at: number): boolean {
  const restricts = tokens
    .slice(at + 1, at + 1 + RESTRICTION_REACH)
    .some((next) => next === 'que' || next === 'qu');
  const subordinate = tokens
    .slice(Math.max(0, at - SUBORDINATE_REACH), at)
    .some((before) => SUBORDINATORS.has(before));
  return restricts || subordinate;
}

/** Whether the token at `at` is the `ne` or the `pas` of « ne pas » behind a governor of an infinitive. */
function isInfinitiveNegation(
  tokens: readonly string[],
  folded: readonly string[],
  at: number,
): boolean {
  const ne = tokens[at] === 'pas' ? at - 1 : at;
  return (
    tokens[ne] === 'ne' &&
    tokens[ne + 1] === 'pas' &&
    INFINITIVE_GOVERNORS.has(folded[ne - 1] ?? '')
  );
}

/**
 * Whether a question is negative (§3.8): the clause that carries it holds a
 * negation - `ne`, the `n` of « n' » except in « n'importe », `pas` (the oral one),
 * `jamais` - or a word of the negative.
 *
 * The lot H of #204 calibrated this on the reference: 18 questions were flagged and
 * none was negative, because the negation was read on the whole sentence. It is read
 * on the clause that carries the question (`questionClause`), and these are no
 * negation of it:
 * - a `ne` with no `pas` and no `jamais` in the clause that is the restriction of
 *   « ne... que » (« il n'y a plus qu'à attendre »), or that a subordinate holds
 *   (« un jour où rien ne presse », « quand plus personne ne vous attend »);
 * - the `ne pas` of an infinitive (« à ne pas le laisser tranquille »);
 * - « dure », when it is the verb durer (« ça dure depuis longtemps ») and not the
 *   hardness of the negative words.
 * A denial that is the question's own stays one: « Qu'est-ce qui n'a pas marché ? »,
 * « Vous n'avez jamais eu peur ? », « Qu'est-ce que vous faites quand ça ne marche
 * pas ? » - the `pas` is there, and a `pas` makes every `ne` of the clause count.
 *
 * The negations are read BEFORE the diacritics go, like the `ou` of two options:
 * « né » folds to `ne` and is a birth, not a negation. The words of the negative
 * are compared without diacritics, as the cahier says, since they are the ones
 * a person types without.
 */
function isNegativeQuestion(question: string): boolean {
  const tokens = lintWords(questionClause(question));
  const folded = tokens.map((token) => withoutDiacritics(token));
  const denies = tokens.some(
    (token, at) =>
      (token === 'pas' || token === 'jamais') && !isInfinitiveNegation(tokens, folded, at),
  );
  if (denies) return true;
  return folded.some((word, at) => {
    if (isInfinitiveNegation(tokens, folded, at)) return false;
    if (isNe(tokens, at)) return !isNeApart(tokens, at);
    if (word === 'dure' && SUBJECTS_OF_DURER.has(folded[at - 1] ?? '')) return false;
    return FOLDED_NEGATIVE_WORDS.has(word);
  });
}

/**
 * Whether a question offers two options (§3.8): it holds the token `ou`. Read
 * BEFORE the diacritics are removed, so « où » is not one.
 */
function offersTwoOptions(question: string): boolean {
  return lintWords(question).includes('ou');
}

/** The whole words of a text, with their place in it; a hyphen only binds two of them. */
const WORD_SPAN = /[\p{L}\p{N}]+(?:-+[\p{L}\p{N}]+)*/gu;
const STARTS_WITH_A_CAPITAL = /^\p{Lu}/u;

/**
 * The names of a first name, to be compared: its folded words of two letters or
 * more (a name typed in two words counts for each, and a lone initial would be
 * the `j` of every « j'ai »).
 */
function nameWords(name: string | null): string[] {
  if (name === null) return [];
  return [...new Set(comparisonWords(name).filter((token) => token.length >= 2))];
}

/**
 * Where a first name is mentioned in a message: a whole word, compared without
 * diacritics, whose occurrence starts with a capital - « Claire » says the name,
 * « claire » is an adjective. The first such place, or null.
 */
function capitalisedMention(text: string, names: readonly string[]): number | null {
  if (names.length === 0) return null;
  for (const word of text.matchAll(WORD_SPAN)) {
    if (!STARTS_WITH_A_CAPITAL.test(word[0])) continue;
    if (names.includes(withoutDiacritics(word[0].toLowerCase()))) return word.index;
  }
  return null;
}

/**
 * Whether a reply of the person says one of the names, casing and diacritics
 * aside. A compound is said by each of its parts: « Jean-Pierre » says « Jean »,
 * whatever the ticket kept of it.
 */
function saysName(reply: string, names: readonly string[]): boolean {
  return comparisonWords(reply).some(
    (token) => names.includes(token) || token.split('-').some((part) => names.includes(part)),
  );
}

/** The ways to ask who the character is, as runs of tokens: « vous êtes qui », « t'es qui ». */
const WHO_ARE_YOU: readonly (readonly string[])[] = [
  ['qui', 'etes-vous'],
  ['qui', 'es-tu'],
  ['qui', 'etes', 'vous'],
  ['qui', 'es', 'tu'],
  ['vous', 'etes', 'qui'],
  ['tu', 'es', 'qui'],
  ['t', 'es', 'qui'],
];

function holdsRun(tokens: readonly string[], run: readonly string[]): boolean {
  return tokens.some((_, at) => run.every((word, offset) => tokens[at + offset] === word));
}

/**
 * Whether the last answer of the person asks for the name of the character
 * (§5.3, V14): the words `prénom`, `nom`, or one that begins with `appel` -
 * « vous vous appelez » - or a way to ask who one is. A character that gives its
 * name when it is asked it is not introducing itself.
 */
function asksForName(reply: string | null): boolean {
  if (reply === null) return false;
  const tokens = comparisonWords(reply);
  if (tokens.some((token) => token === 'prenom' || token === 'nom' || token.startsWith('appel'))) {
    return true;
  }
  return WHO_ARE_YOU.some((run) => holdsRun(tokens, run));
}

/**
 * The first-person markers of the singular (§3.8): the speaker names itself as the
 * one who lives and owns - `je`, `moi`, `mon`, `ma`, `mes`. Read on the folded
 * tokens, so « j'ai » is `j`, and « racontez-moi » - one token, the hyphen binds - is
 * nobody confiding.
 *
 * `me` and `m` are NOT markers: « ça me touche », « voilà qui m'intrigue », « même
 * une toute petite chose me va » say the effect on the character, which is a
 * reaction, and a confidence is a fact of its life (§3.8). The lot H of #204 took
 * them out when the reference showed reactions counted as confidences and no
 * confidence lost.
 */
const FIRST_PERSON = new Set(['je', 'j', 'moi', 'mon', 'ma', 'mes']);

/**
 * The formulas of listening and of meta, which say « je » without confiding:
 * « je vois », « je comprends », « attendez, que je me l'imagine ». Perception,
 * belief and knowledge turned on the person's words are the same thing, and the lot
 * H of #204 added them after the reference: « je vous crois », « je veux bien le
 * croire », « je la vois d'ici », « j'ai bien vu », « j'ai bien l'image », « je m'en
 * doutais », « il me semble ». Each says the character heard, and tells nothing of
 * its life.
 *
 * The longest first, so « que je me l'imagine » is taken whole before « je me » could
 * be read.
 */
const LISTENING_FORMULAS: readonly (readonly string[])[] = [
  ['que', 'je', 'me', 'l', 'imagine'],
  ['je', 'veux', 'bien', 'le', 'croire'],
  ['j', 'ai', 'bien', 'l', 'image'],
  ['je', 'm', 'en', 'doutais'],
  ['j', 'ai', 'bien', 'vu'],
  ['je', 'me', 'doute'],
  ['je', 'me', 'demande'],
  ['je', 'vous', 'crois'],
  ['je', 'vous', 'vois'],
  ['je', 'le', 'crois'],
  ['je', 'le', 'vois'],
  ['je', 'la', 'vois'],
  ['je', 'les', 'vois'],
  ['il', 'me', 'semble'],
  ['j', 'imagine'],
  ['je', 'vois'],
  ['je', 'comprends'],
  ['je', 'devine'],
  ['je', 'crois'],
  ['je', 'pense'],
];

/** The tokens of a sentence once its formulas of listening are taken out. */
function withoutListeningFormulas(tokens: readonly string[]): string[] {
  const kept: string[] = [];
  let at = 0;
  while (at < tokens.length) {
    const formula = LISTENING_FORMULAS.find((candidate) =>
      candidate.every((word, offset) => tokens[at + offset] === word),
    );
    if (formula !== undefined) {
      at += formula.length;
      continue;
    }
    const token = tokens[at];
    if (token !== undefined) kept.push(token);
    at += 1;
  }
  return kept;
}

/**
 * Whether sentences hold a CONFIDENCE (§3.8): one that is not a question and
 * says « je » - or « moi », « mon », « ma » - beyond a formula of listening.
 */
function holdsConfidence(sentences: readonly Span[]): boolean {
  return sentences.some(
    (sentence) =>
      !isQuestion(sentence.text) &&
      withoutListeningFormulas(comparisonWords(sentence.text)).some((token) =>
        FIRST_PERSON.has(token),
      ),
  );
}

/* ------------------------------------------------------------- the input/output */

/** One message of the interviewer, with the answer that followed it. */
export interface LintMessage {
  /** A turn of the protocol, or a bubble of a page played in the conversation (#186). */
  readonly kind: 'turn' | 'bubble';
  /** The number of the turn, 1-based like the Sessions screen (`index + 1`); null for a bubble. */
  readonly turn: number | null;
  /** The rank of the bubble among the bubbles, 1-based; null for a turn. */
  readonly bubble: number | null;
  /** The step of the turn; null for a bubble. */
  readonly stepId: string | null;
  /** The frozen name of the page of a bubble; null for a turn. */
  readonly pageName: string | null;
  /** The turn of a farewell step (`content.steps[].farewell`). */
  readonly farewell: boolean;
  /** The interviewer's message. */
  readonly message: string;
  /** The answer of the person that followed; null at the farewell, on a bubble, or while it is awaited. */
  readonly reply: string | null;
}

export interface LintInput {
  /** In the order of the conversation, the rereads on entry left out. */
  readonly messages: readonly LintMessage[];
  /** The first name typed on the ticket; null when it is not known. */
  readonly personFirstName: string | null;
  /** The first name the persona shows (its `firstName`); null when it is not known. */
  readonly personaFirstName: string | null;
  /** The directions of the frozen protocol: each step's and each objective's. */
  readonly directions: readonly string[];
  /** The questions imposed word for word, which leave « question recopiée ». */
  readonly imposed: readonly string[];
}

export interface LintFault {
  readonly code: HardFaultCode;
  readonly turn: number | null;
  readonly bubble: number | null;
  readonly stepId: string | null;
  /** The sentence at fault, 160 characters at most. */
  readonly excerpt: string;
}

/**
 * What a message carries beside its faults, to be read without opening the
 * blocks: `p12` (the one negative question its step allows), `r2` (the one
 * question with two options its step allows), `farewell`, `confidence`,
 * `confidence-without-bubble` (a confidence that shares its paragraph with the
 * question) and `mould` (a reaction, then a question).
 */
export type LintSignal =
  | 'p12'
  | 'r2'
  | 'farewell'
  | 'confidence'
  | 'confidence-without-bubble'
  | 'mould';

export interface MessageLint {
  readonly kind: 'turn' | 'bubble';
  readonly turn: number | null;
  readonly bubble: number | null;
  readonly stepId: string | null;
  readonly pageName: string | null;
  readonly words: number;
  readonly signals: readonly LintSignal[];
  readonly hard: readonly HardFaultCode[];
  /** The longest run of a question this message copies from a direction. */
  readonly copiedRun: string | null;
  /** The longest run of this message that echoes an earlier answer of the person. */
  readonly echoRun: string | null;
  /** The turn of the answer the echo comes from. */
  readonly echoFromTurn: number | null;
  /**
   * The opening of a turn: its first token, without diacritics, or its first two when
   * the first is a function word (« et vous »).
   */
  readonly opening: string | null;
}

/** « Confidences » (§3.8): two at most, never two turns in a row, from the moment on. */
export interface ConfidencesLint {
  readonly value: number;
  readonly consecutivePairs: number;
  readonly turns: readonly number[];
  readonly met: boolean;
}

/** « Mots par message »: the median and the maximum. Null when there is no turn. */
export interface WordsLint {
  readonly median: number | null;
  readonly max: number | null;
  readonly met: boolean | null;
}

/** « Réaction puis question » (§1): the share of the turns, and the longest streak. */
export interface MouldLint {
  readonly share: number | null;
  readonly longestStreak: number;
  readonly turns: readonly number[];
  readonly met: boolean | null;
}

/** « Question recopiée »: `evaluated` is false when the protocol has no direction to be copied from. */
export interface CopiedLint {
  readonly evaluated: boolean;
  /** The turns with a copied question. */
  readonly value: number;
  /** The turns (the farewell apart) that ask at least one question. */
  readonly of: number;
  readonly share: number | null;
  readonly longestRun: string | null;
  readonly inTurn: number | null;
  readonly turns: readonly number[];
  readonly met: boolean | null;
}

/** « Écho exact » (P8): the turns that take four words of an earlier answer as they stand. */
export interface EchoLint {
  readonly value: number;
  readonly longestRun: string | null;
  readonly inTurn: number | null;
  readonly fromTurn: number | null;
  readonly turns: readonly number[];
  readonly met: boolean;
}

/** « Même ouverture »: the turns that open as one of the two before them. */
export interface OpeningsLint {
  readonly value: number;
  readonly turns: readonly number[];
  readonly met: boolean;
}

export interface SoftLint {
  readonly confidences: ConfidencesLint;
  readonly words: WordsLint;
  readonly mould: MouldLint;
  readonly copied: CopiedLint;
  readonly echo: EchoLint;
  readonly openings: OpeningsLint;
  /** How many of the targets are met; one that could not be evaluated is not. */
  readonly met: number;
  readonly total: 6;
}

/**
 * « Part de parole », a reading: the interviewer's share of the words of the
 * conversation, beside the median of the person's answers, since the share
 * follows those more than the interviewer's. Null when nobody spoke.
 */
export interface ShareReading {
  readonly value: number | null;
  /** The median of the words of the person's answers; null when no answer was written. */
  readonly personMedian: number | null;
}

/**
 * « Confidence sans sa bulle » (§4.7), a reading: the turns counted as confidences
 * (`soft.confidences`) where no paragraph holds the confidence apart from the
 * question. The screen cuts a message at the empty line, so a paragraph that holds
 * a confidence and no question is the bubble of its own V19 asks for. The lot J
 * brings it to zero.
 */
export interface BubbleReading {
  readonly value: number;
  readonly turns: readonly number[];
}

/** What the linter reads and no target bounds (§15.1): the gate leaves them out. */
export interface LintReadings {
  readonly share: ShareReading;
  readonly confidencesWithoutBubble: BubbleReading;
}

export interface ConversationLint {
  readonly counts: {
    readonly turns: number;
    readonly bubbles: number;
    /** The words of the interviewer over the turns - the bubbles are not in it. */
    readonly interviewerWords: number;
    /** The words of the person's answers. */
    readonly personWords: number;
  };
  readonly hard: {
    readonly total: number;
    readonly byCode: Readonly<Record<HardFaultCode, number>>;
    readonly faults: readonly LintFault[];
  };
  readonly soft: SoftLint;
  readonly readings: LintReadings;
  readonly messages: readonly MessageLint[];
}

/* ---------------------------------------------------------------- the analysis */

/** A message with everything the checks read of it, worked out once. */
interface Analysed {
  readonly source: LintMessage;
  /** The text in its composed form: every position below is read in it. */
  readonly text: string;
  readonly tokens: string[];
  readonly folded: string[];
  readonly sentences: Span[];
  readonly questions: Span[];
}

function analyse(source: LintMessage): Analysed {
  const text = source.message.normalize('NFC');
  const tokens = lintWords(text);
  const sentences = sentenceSpans(text);
  return {
    source,
    text,
    tokens,
    folded: tokens.map((token) => withoutDiacritics(token)),
    sentences,
    questions: sentences.filter((sentence) => isQuestion(sentence.text)),
  };
}

interface Names {
  readonly person: readonly string[];
  readonly persona: readonly string[];
}

/** What the hard checks carry from one message to the next. */
interface HardState {
  readonly priorReplies: string[];
  lastReply: string | null;
  /** A negative question has been met: the exception of P12 is for the FIRST one only. */
  negativeSeen: boolean;
  /** The follow-up with leads has been asked in `R2_STEP_ID`: the exception of R2 is spent. */
  leadsSpent: boolean;
}

interface CodedExcerpt {
  readonly code: HardFaultCode;
  readonly excerpt: string;
}

/**
 * The hard faults of one message, with their excerpt - and whether the message
 * carries the one negative question P12 lets through, or the one question with two
 * options R2 does.
 *
 * P12 is « une fois au plus », and only in `P12_STEP_ID`, and it is read as the
 * cahier words it: the FIRST negative question of the conversation is no fault
 * when it stands in that step, and every other is one. So a negative question
 * met earlier, in another step, is a fault of its own and the one of Le sujet
 * that comes after it is the second, hence a fault too. A question that only LOOKS
 * negative (`isNegativeQuestion` reads the clause that carries it, not the whole
 * sentence) spends nothing: the exception is for the first REAL negative question.
 *
 * R2 is read on the same model, with one difference the decision of 2026-10-24
 * made: the FIRST question with two options asked in `R2_STEP_ID` is no fault, and
 * every other is one, but a question with two options asked in another step is a
 * fault that spends nothing. The follow-up with leads belongs to Le succès,
 * whatever was asked before it.
 */
function hardFaultsOf(
  message: Analysed,
  state: HardState,
  names: Names,
): { faults: CodedExcerpt[]; p12: boolean; r2: boolean } {
  const { text, sentences, questions } = message;
  const faults: CodedExcerpt[] = [];
  let p12 = false;
  let r2 = false;

  const lead = LEADING_WORD.exec(text);
  const leadWord = lead?.[1];
  if (
    lead !== null &&
    leadWord !== undefined &&
    isInterjection(leadWord, text.slice(lead[0].length))
  ) {
    faults.push({ code: 'interjection', excerpt: excerptOf(sentences[0]?.text ?? text) });
  }

  const quoteAt = text.search(QUOTES);
  if (quoteAt >= 0) {
    faults.push({ code: 'quotes', excerpt: sentenceExcerpt(text, sentences, quoteAt) });
  }

  const [first, second] = [...text.matchAll(QUESTION_MARKS)];
  if (first !== undefined && second !== undefined) {
    const from = spanAround(sentences, first.index)?.start ?? first.index;
    const to = spanAround(sentences, second.index)?.end ?? second.index + second[0].length;
    faults.push({ code: 'two-questions', excerpt: excerptOf(text.slice(from, to)) });
  }

  let negative: Span | null = null;
  let options: Span | null = null;
  for (const question of questions) {
    if (isNegativeQuestion(question.text)) {
      const first = !state.negativeSeen;
      state.negativeSeen = true;
      if (first && message.source.stepId === P12_STEP_ID) p12 = true;
      else if (negative === null) negative = question;
    }
    if (offersTwoOptions(question.text)) {
      if (!state.leadsSpent && message.source.stepId === R2_STEP_ID) {
        state.leadsSpent = true;
        r2 = true;
      } else if (options === null) options = question;
    }
  }
  if (negative !== null)
    faults.push({ code: 'negative-question', excerpt: excerptOf(negative.text) });
  if (options !== null) faults.push({ code: 'two-options', excerpt: excerptOf(options.text) });

  const personAt = capitalisedMention(text, names.person);
  if (personAt !== null && !state.priorReplies.some((reply) => saysName(reply, names.person))) {
    faults.push({ code: 'first-name-unsaid', excerpt: sentenceExcerpt(text, sentences, personAt) });
  }

  const personaAt = capitalisedMention(text, names.persona);
  if (personaAt !== null && !asksForName(state.lastReply)) {
    faults.push({
      code: 'self-introduction',
      excerpt: sentenceExcerpt(text, sentences, personaAt),
    });
  }

  return { faults, p12, r2 };
}

/* --------------------------------------------------------------- the soft checks */

/** A turn of the protocol as the soft checks read it. */
interface TurnView {
  readonly at: number;
  readonly number: number;
  readonly message: Analysed;
  readonly farewell: boolean;
}

/** What a message gets marked with by the soft checks, to be written on its line. */
interface Marks {
  confidence: boolean;
  withoutBubble: boolean;
  mould: boolean;
  copiedRun: string | null;
  echoRun: string | null;
  echoFromTurn: number | null;
  opening: string | null;
}

function emptyMarks(): Marks {
  return {
    confidence: false,
    withoutBubble: false,
    mould: false,
    copiedRun: null,
    echoRun: null,
    echoFromTurn: null,
    opening: null,
  };
}

/** A share, to four decimals: what is shown. The verdict is taken on the exact one. */
function shown(value: number | null): number | null {
  return value === null ? null : Math.round(value * 10_000) / 10_000;
}

function ratio(part: number, whole: number): number | null {
  return whole === 0 ? null : part / whole;
}

function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const upper = sorted[middle] ?? 0;
  if (sorted.length % 2 === 1) return upper;
  return ((sorted[middle - 1] ?? upper) + upper) / 2;
}

/** The longest streak of consecutive `true` in a list. */
function longestStreak(flags: readonly boolean[]): number {
  let best = 0;
  let current = 0;
  for (const flag of flags) {
    current = flag ? current + 1 : 0;
    best = Math.max(best, current);
  }
  return best;
}

/** The text of a run, written with the lower case tokens of the message it was found in. */
function runText(message: Analysed, run: Run): string {
  return message.tokens.slice(run.from, run.from + run.length).join(' ');
}

/** The key under which a question is looked for among the imposed ones: its folded tokens, spaced. */
function paddedKey(tokens: readonly string[]): string {
  return ` ${tokens.join(' ')} `;
}

/**
 * « Confidences » (§3.8). The phase of the moment opens at the first turn of
 * `MOMENT_STEP_ID`; before it nothing counts, and the farewell never does.
 * A session with no such turn has none: value 0, met.
 */
function confidencesOf(turns: readonly TurnView[], marks: Marks[]): ConfidencesLint {
  const opens = turns.findIndex((turn) => turn.message.source.stepId === MOMENT_STEP_ID);
  const phase = opens === -1 ? [] : turns.slice(opens).filter((turn) => !turn.farewell);
  const flags = phase.map((turn) => holdsConfidence(turn.message.sentences));
  const confided = phase.filter((_, at) => flags[at] === true);
  for (const turn of confided) {
    const mark = marks[turn.at];
    if (mark !== undefined) mark.confidence = true;
  }
  const consecutivePairs = flags.filter((flag, at) => flag && flags[at + 1] === true).length;
  return {
    value: confided.length,
    consecutivePairs,
    turns: confided.map((turn) => turn.number),
    met:
      confided.length <= LINT_TARGETS.confidences.max &&
      consecutivePairs <= LINT_TARGETS.confidences.consecutivePairsMax,
  };
}

/**
 * « Confidence sans sa bulle » (§4.7): of the turns `confidencesOf` marked, those
 * where no paragraph holds a confidence and no question. A turn of one paragraph
 * that confides and asks is one; so is a turn in two whose confidence went with the
 * question. Read after `confidencesOf`, whose marks it reads.
 */
function confidencesWithoutBubbleOf(turns: readonly TurnView[], marks: Marks[]): BubbleReading {
  const without = turns.filter((turn) => {
    if (marks[turn.at]?.confidence !== true) return false;
    return !lintParagraphs(turn.message.text).some((paragraph) => {
      const sentences = sentenceSpans(paragraph);
      return holdsConfidence(sentences) && !sentences.some((sentence) => isQuestion(sentence.text));
    });
  });
  for (const turn of without) {
    const mark = marks[turn.at];
    if (mark !== undefined) mark.withoutBubble = true;
  }
  return { value: without.length, turns: without.map((turn) => turn.number) };
}

function wordsOfTurns(turns: readonly TurnView[]): WordsLint {
  const counts = turns.map((turn) => turn.message.tokens.length);
  const middle = median(counts);
  const biggest = counts.length === 0 ? null : Math.max(...counts);
  return {
    median: middle,
    max: biggest,
    met:
      middle === null || biggest === null
        ? null
        : middle <= LINT_TARGETS.words.medianMax && biggest <= LINT_TARGETS.words.max,
  };
}

/** « Réaction puis question » (§1): more than one sentence, the last a question, the first not. */
function mouldOf(exchanges: readonly TurnView[], marks: Marks[]): MouldLint {
  const flags = exchanges.map(({ message }) => {
    const { sentences } = message;
    const first = sentences[0];
    const last = sentences[sentences.length - 1];
    return (
      sentences.length > 1 &&
      first !== undefined &&
      last !== undefined &&
      isQuestion(last.text) &&
      !isQuestion(first.text)
    );
  });
  const moulded = exchanges.filter((_, at) => flags[at] === true);
  for (const turn of moulded) {
    const mark = marks[turn.at];
    if (mark !== undefined) mark.mould = true;
  }
  const share = ratio(moulded.length, exchanges.length);
  const streak = longestStreak(flags);
  return {
    share: shown(share),
    longestStreak: streak,
    turns: moulded.map((turn) => turn.number),
    met:
      share === null
        ? null
        : share <= LINT_TARGETS.mould.shareMax && streak <= LINT_TARGETS.mould.longestStreakMax,
  };
}

/**
 * « Question recopiée »: a question that shares with a direction of the protocol a
 * run of five tokens or more, one of them a full word, that no imposed question
 * holds. The directions are one index, the farewell and the turns with no question
 * are out, and a protocol with no direction is not evaluated.
 */
function copiedOf(
  exchanges: readonly TurnView[],
  directions: readonly string[],
  imposed: readonly string[],
  marks: Marks[],
): CopiedLint {
  if (directions.length === 0) {
    return {
      evaluated: false,
      value: 0,
      of: 0,
      share: null,
      longestRun: null,
      inTurn: null,
      turns: [],
      met: null,
    };
  }
  const index = newGramIndex(LINT_TARGETS.copied.minTokens);
  directions.forEach((direction, at) => addSource(index, comparisonWords(direction), at));
  const imposedKeys = imposed.map((question) => paddedKey(comparisonWords(question)));

  const asking = exchanges.filter((turn) => turn.message.questions.length > 0);
  const copied: TurnView[] = [];
  let longest: { run: string; turn: number } | null = null;
  let longestLength = 0;
  for (const turn of asking) {
    let best: { run: Run; question: Span } | null = null;
    for (const question of turn.message.questions) {
      const tokens = lintWords(question.text);
      const folded = tokens.map((token) => withoutDiacritics(token));
      const run = longestFullRun(
        runsAgainst(index, folded),
        folded,
        (shared) => !imposedKeys.some((key) => key.includes(paddedKey(shared))),
      );
      if (run !== null && (best === null || run.length > best.run.length)) {
        best = { run, question };
      }
    }
    if (best === null) continue;
    copied.push(turn);
    const text = lintWords(best.question.text)
      .slice(best.run.from, best.run.from + best.run.length)
      .join(' ');
    const mark = marks[turn.at];
    if (mark !== undefined) mark.copiedRun = text;
    if (best.run.length > longestLength) {
      longestLength = best.run.length;
      longest = { run: text, turn: turn.number };
    }
  }
  const share = ratio(copied.length, asking.length);
  return {
    evaluated: true,
    value: copied.length,
    of: asking.length,
    share: shown(share),
    longestRun: longest?.run ?? null,
    inTurn: longest?.turn ?? null,
    turns: copied.map((turn) => turn.number),
    met: share === null ? null : share <= LINT_TARGETS.copied.shareMax,
  };
}

/**
 * « Écho exact » (P8): a turn that shares with an EARLIER answer of the person -
 * any of them - a run of four tokens or more with a full word in it. The answers
 * are put in the index as the turns go by, so a turn only meets what came before.
 */
function echoOf(turns: readonly TurnView[], marks: Marks[]): EchoLint {
  const index = newGramIndex(LINT_TARGETS.echo.minTokens);
  const echoing: TurnView[] = [];
  let longest: { run: string; turn: number; from: number } | null = null;
  let longestLength = 0;
  for (const turn of turns) {
    const { message } = turn;
    if (!turn.farewell) {
      const run = longestFullRun(runsAgainst(index, message.folded), message.folded);
      if (run !== null) {
        echoing.push(turn);
        const text = runText(message, run);
        const mark = marks[turn.at];
        if (mark !== undefined) {
          mark.echoRun = text;
          mark.echoFromTurn = run.source;
        }
        if (run.length > longestLength) {
          longestLength = run.length;
          longest = { run: text, turn: turn.number, from: run.source };
        }
      }
    }
    const { reply } = message.source;
    if (reply !== null) addSource(index, comparisonWords(reply), turn.number);
  }
  return {
    value: echoing.length,
    longestRun: longest?.run ?? null,
    inTurn: longest?.turn ?? null,
    fromTurn: longest?.from ?? null,
    turns: echoing.map((turn) => turn.number),
    met: echoing.length <= LINT_TARGETS.echo.max,
  };
}

/**
 * The opening of a message: its first token, without diacritics - or its first TWO
 * when that token is a function word. « Le », « et », « c' », « qu' » begin a large
 * part of the French sentences and are nobody's tic: « Le vendredi soir... » after
 * « Le train de 18 h 04... » repeats nothing, where « Merci, ... » after « Merci
 * beaucoup ... » and « Et vous, ... » after « Et vous, ... » do. The first token
 * alone flagged every article on the reference of #204 (the lot H found 32
 * violations, all of them function words); the first two alone would let « Merci
 * beaucoup » and « Merci, » count as different openings.
 *
 * A message of one token has that one; a message with none has no opening.
 */
function openingOf(message: Analysed): string | null {
  const [first, second] = message.folded;
  if (first === undefined) return null;
  return isStopWord(first) && second !== undefined ? `${first} ${second}` : first;
}

/**
 * « Même ouverture »: a turn that opens as one of the two before it - see
 * `openingOf` - is a violation. The farewell counts: it is a message like the others.
 */
function openingsOf(turns: readonly TurnView[], marks: Marks[]): OpeningsLint {
  const openings = turns.map((turn) => openingOf(turn.message));
  const repeated: TurnView[] = [];
  turns.forEach((turn, at) => {
    const opening = openings[at] ?? null;
    const mark = marks[turn.at];
    if (mark !== undefined) mark.opening = opening;
    if (opening === null) return;
    if (opening === openings[at - 1] || opening === openings[at - 2]) repeated.push(turn);
  });
  return {
    value: repeated.length,
    turns: repeated.map((turn) => turn.number),
    met: repeated.length <= LINT_TARGETS.openings.max,
  };
}

/* ------------------------------------------------------------------ the linter */

function emptyCounts(): Record<HardFaultCode, number> {
  return {
    interjection: 0,
    quotes: 0,
    'two-questions': 0,
    'negative-question': 0,
    'two-options': 0,
    'first-name-unsaid': 0,
    'self-introduction': 0,
  };
}

/**
 * Lints a conversation: the hard faults of every message, and the soft targets and
 * the readings of the conversation of the protocol (§15.1).
 *
 * Pure, and the same for the same input: a message is read in the order of the
 * conversation, and what it is compared with - the answers before it, the
 * directions, the imposed questions - is only what the input holds.
 */
export function lintConversation(input: LintInput): ConversationLint {
  const names: Names = {
    person: nameWords(input.personFirstName),
    persona: nameWords(input.personaFirstName),
  };
  const directions = input.directions.filter((direction) => direction.trim() !== '');
  const analysed = input.messages.map(analyse);
  const marks = analysed.map(emptyMarks);

  // The hard faults, message by message, in the order of the conversation: what a
  // message may say depends on the answers that came before it.
  const state: HardState = {
    priorReplies: [],
    lastReply: null,
    negativeSeen: false,
    leadsSpent: false,
  };
  const hard = analysed.map((message) => {
    const found = hardFaultsOf(message, state, names);
    const { reply } = message.source;
    if (reply !== null) {
      state.priorReplies.push(reply);
      state.lastReply = reply;
    }
    return found;
  });

  // The turns of the protocol: the only ones the soft targets read.
  const turns: TurnView[] = [];
  analysed.forEach((message, at) => {
    if (message.source.kind !== 'turn') return;
    turns.push({
      at,
      number: message.source.turn ?? turns.length + 1,
      message,
      farewell: message.source.farewell,
    });
  });
  const exchanges = turns.filter((turn) => !turn.farewell);

  const confidences = confidencesOf(turns, marks);
  const confidencesWithoutBubble = confidencesWithoutBubbleOf(turns, marks);
  const words = wordsOfTurns(turns);
  const interviewerWords = turns.reduce((sum, turn) => sum + turn.message.tokens.length, 0);
  const answers = turns.flatMap((turn) =>
    turn.message.source.reply === null ? [] : [lintWords(turn.message.source.reply).length],
  );
  const personWords = answers.reduce((sum, count) => sum + count, 0);
  const share: ShareReading = {
    value: shown(ratio(interviewerWords, interviewerWords + personWords)),
    personMedian: median(answers),
  };
  const mould = mouldOf(exchanges, marks);
  const copied = copiedOf(exchanges, directions, input.imposed, marks);
  const echo = echoOf(turns, marks);
  const openings = openingsOf(turns, marks);

  const met = [confidences.met, words.met, mould.met, copied.met, echo.met, openings.met].filter(
    (verdict) => verdict === true,
  ).length;

  const byCode = emptyCounts();
  const faults: LintFault[] = [];
  analysed.forEach((message, at) => {
    for (const fault of hard[at]?.faults ?? []) {
      byCode[fault.code] += 1;
      faults.push({
        code: fault.code,
        turn: message.source.turn,
        bubble: message.source.bubble,
        stepId: message.source.stepId,
        excerpt: fault.excerpt,
      });
    }
  });

  const lines = analysed.map((message, at): MessageLint => {
    const mark = marks[at] ?? emptyMarks();
    const signals: LintSignal[] = [];
    if (hard[at]?.p12 === true) signals.push('p12');
    if (hard[at]?.r2 === true) signals.push('r2');
    if (message.source.farewell) signals.push('farewell');
    if (mark.confidence) signals.push('confidence');
    if (mark.withoutBubble) signals.push('confidence-without-bubble');
    if (mark.mould) signals.push('mould');
    return {
      kind: message.source.kind,
      turn: message.source.turn,
      bubble: message.source.bubble,
      stepId: message.source.stepId,
      pageName: message.source.pageName,
      words: message.tokens.length,
      signals,
      hard: (hard[at]?.faults ?? []).map((fault) => fault.code),
      copiedRun: mark.copiedRun,
      echoRun: mark.echoRun,
      echoFromTurn: mark.echoFromTurn,
      opening: mark.opening,
    };
  });

  return {
    counts: {
      turns: turns.length,
      bubbles: analysed.length - turns.length,
      interviewerWords,
      personWords,
    },
    hard: { total: faults.length, byCode, faults },
    soft: {
      confidences,
      words,
      mould,
      copied,
      echo,
      openings,
      met,
      total: 6,
    },
    readings: { share, confidencesWithoutBubble },
    messages: lines,
  };
}
